import cx from 'classnames';

import {
  Icon,
  SettingsGroup,
  SettingsGroupButtonItem,
} from '@sb/design-system';

import type { SettingsSection } from '../settings/settingsSections';
import { settingsSectionUrl } from '../settings/settingsSections';

interface RestoreFollowUpPanelProps {
  /** Dismisses the restore and sends the operator to the section. */
  onOpenSection: (url: string) => void;
  disabled?: boolean;
}

const FOLLOW_UPS: Array<{
  section: SettingsSection;
  icon: 'shield' | 'powerplug';
  label: string;
  detail: string;
  testId: string;
}> = [
  {
    section: 'safety',
    icon: 'shield',
    label: 'Check Safety settings',
    detail:
      'Speed, force and zone limits now come from the backup, not from this cell.',
    testId: 'restore-confirmation-followup-safety-button',
  },
  {
    section: 'equipment',
    icon: 'powerplug',
    label: 'Check Equipment',
    detail:
      'The restored configuration may name a gripper or tool this robot does not have fitted.',
    testId: 'restore-confirmation-followup-equipment-button',
  },
];

/**
 * What the operator has to check before the robot is used again.
 *
 * A restore replaces the safety configuration and the equipment list
 * wholesale, from a backup that was taken on a different robot in a
 * different cell. Both describe how hard this arm is allowed to move and
 * what it believes is bolted to it, so neither can be assumed to still
 * describe reality — and neither is checked by anything upstream.
 *
 * Seen after the operator has logged back in: the restore drops the
 * session and reloads, and this modal returns once they are in again.
 */
export function RestoreFollowUpPanel({
  onOpenSection,
  disabled,
}: RestoreFollowUpPanelProps) {
  return (
    <section
      className="tw-flex tw-flex-col"
      data-testid="restore-confirmation-followup-region"
    >
      <h5 className="tw-heading-40 tw-pl-16">Before using this robot again</h5>

      <p className="tw-text-13 tw-text-label-secondary tw-px-16 tw-pb-8">
        This robot is running the backup&apos;s settings now, including its
        accounts and passcode. Check both of these before you use it.
      </p>

      <SettingsGroup>
        {FOLLOW_UPS.map(({ section, icon, label, detail, testId }) => (
          <SettingsGroupButtonItem
            key={section}
            data-testid={testId}
            disabled={disabled}
            onClick={() => onOpenSection(settingsSectionUrl(section))}
          >
            <Icon kind={icon} className="tw-icon-22 tw-shrink-0" />

            {/* The row truncates to a single line by default; these rows
                exist for the sentence under the label, so let it wrap. */}
            <span
              className={cx(
                'tw-flex-1',
                'tw-flex',
                'tw-flex-col',
                'tw-gap-2',
                'tw-py-8',
                'tw-text-left',
                'tw-whitespace-normal',
              )}
            >
              <span>{label}</span>
              <span className="tw-text-13 tw-text-label-secondary">
                {detail}
              </span>
            </span>

            <Icon kind="chevronRight" className="tw-icon-22 tw-shrink-0" />
          </SettingsGroupButtonItem>
        ))}
      </SettingsGroup>
    </section>
  );
}
